import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Snackbar } from '@sunrun/experience-ui-components';
import { useFlagsmith, useFlagsmithLoading } from 'hooks/useFlagsmith';
import { ClockWorkHoursModal } from 'components/molecules/ClockWorkHoursModal';
import { LogHoursReminderModalV1 } from 'components/molecules/LogHoursReminderModalV1';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { CA_STREET_SALES_SCHEDULER } from 'constants/workday';
import { useWorkdayLogHoursModal } from 'hooks/useWorkdayLogHoursModal';
import { useUserProfile } from 'providers/UserProfile';

type Toast = {
  type: 'success' | 'error';
  message: string;
};

const LogHoursReminderModal = () => {
  const { userProfile } = useUserProfile();
  const flagsmith = useFlagsmith();
  const flagsmithLoading = useFlagsmithLoading();
  const { isOpen, openModal, closeModal } = useWorkdayLogHoursModal();
  const [toast, setToast] = useState<Toast>();

  const isCaStreetSalesScheduler =
    userProfile?.title === CA_STREET_SALES_SCHEDULER;

  const contactId = userProfile?.contactId;
  const flagReady =
    Boolean(contactId) &&
    flagsmith.identity === contactId &&
    !flagsmithLoading?.isFetching;

  const showClockWorkHours =
    flagReady && flagsmith.hasFeature(FEATURE_FLAGS.WORKDAY_HOURS_WORKFLOW);
  const showV1Reminder = flagReady && !showClockWorkHours;
  const showHoursModal = isOpen && isCaStreetSalesScheduler;

  // Remind schedulers to log their hours as soon as the dashboard loads.
  useEffect(() => {
    if (isCaStreetSalesScheduler) {
      openModal();
    } else {
      closeModal();
    }
  }, [isCaStreetSalesScheduler, openModal, closeModal]);

  const handleNotify = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
  }, []);

  return (
    <>
      {showHoursModal && showClockWorkHours && (
        <ClockWorkHoursModal onClose={closeModal} onNotify={handleNotify} />
      )}
      {showHoursModal && showV1Reminder && (
        <LogHoursReminderModalV1 onClose={closeModal} />
      )}
      {toast &&
        createPortal(
          <Snackbar
            open
            onClose={() => setToast(undefined)}
            message={toast.message}
            type={toast.type}
            colorScheme="dark"
          />,
          document.body,
        )}
    </>
  );
};

export { LogHoursReminderModal };
